import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PromoCarousel = ({ promotions = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const hasPromotions = promotions && promotions.length > 0;
  
  const nextSlide = useCallback(() => {
    if (hasPromotions) {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
    }
  }, [hasPromotions, promotions.length]);
  
  const prevSlide = () => {
    if (hasPromotions) {
      setCurrentIndex((prev) => (prev - 1 + promotions.length) % promotions.length);
    }
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };
  
  useEffect(() => {
    if (!isAutoPlaying || !hasPromotions || promotions.length <= 1) return;
    
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, hasPromotions, promotions.length, nextSlide]);
  
  // Don't render anything if there are no promotions
  if (!hasPromotions) {
    return null;
  }
  
  const currentPromo = promotions[currentIndex];
  
  return (
    <div className="relative" data-testid="promo-carousel">
      <Card className="rounded-3xl overflow-hidden bg-white shadow-soft-pop" style={{ border: '5px solid #FFA6A6' }}>
        {/* Image */}
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '1/1' }}>
          <img
            src={currentPromo.image_url}
            alt={currentPromo.title}
            className="w-full h-full object-cover transition-transform duration-500"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?w=800&auto=format&fit=crop&q=60';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          
          {/* Navigation Arrows */}
          {promotions.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-10 h-10 shadow-md"
                onClick={prevSlide}
                data-testid="promo-prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-gray-800" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-10 h-10 shadow-md"
                onClick={nextSlide}
                data-testid="promo-next-btn"
              >
                <ChevronRight className="w-5 h-5 text-gray-800" />
              </Button>
            </>
          )}
        </div>
        
        {/* Content */}
        <CardContent className="p-5">
          <h4 className="font-nunito font-bold text-lg text-gray-900 mb-1">
            {currentPromo.title}
          </h4>
          <p className="text-gray-600 font-quicksand text-sm">
            {currentPromo.description}
          </p>
        </CardContent>
      </Card>
      
      {/* Dots Indicator */}
      {promotions.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {promotions.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${index === currentIndex 
                  ? 'w-6 bg-rose-pop' 
                  : 'bg-rose-soft hover:bg-rose-pop/50'
                }
              `}
              data-testid={`promo-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoCarousel;
